import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase/client';
import type { User } from '../types/user';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  signup: (email: string, password: string, name: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,

      signup: async (email: string, password: string, fullName: string) => {        
        try {
          // Clear any existing state
          set({ isAuthenticated: false, user: null });
          
          const { data, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName
              }
            }
          });

          if (signUpError) throw signUpError;
          if (!data?.user) throw new Error('Signup failed - no user data received');

          // Set auth state
          set({
            isAuthenticated: true,
            user: {
              id: data.user.id,
              email: data.user.email!,
              name: fullName,
              createdAt: new Date(),
              updatedAt: new Date()
            }
          });
        } catch (error: any) {
          console.error('Signup error:', error);
          set({ isAuthenticated: false, user: null });
          
          // Handle specific Supabase errors
          if (error.message === 'User already registered') {
            throw new Error('An account with this email already exists. Please try logging in instead.');
          }
          
          throw new Error(error.message || 'An error occurred during signup');
        }
      },

      login: async (email: string, password: string) => {
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
          });

          if (error) {
            if (error.message === 'Invalid login credentials') {
              throw new Error('Invalid email or password. Please check your credentials and try again.');
            }
            throw new Error(error.message);
          }
          
          if (!data.user) throw new Error('Login failed - no user data received');

          set({
            isAuthenticated: true,
            user: {
              id: data.user.id,
              email: data.user.email!,
              name: data.user.user_metadata.full_name || data.user.email!.split('@')[0],
              createdAt: new Date(data.user.created_at),
              updatedAt: new Date(data.user.updated_at)
            }
          });
        } catch (error: any) {
          console.error('Login error:', error);
          set({ isAuthenticated: false, user: null });
          throw error;
        }
      },

      logout: async () => {
        try {
          await supabase.auth.signOut();
          set({ isAuthenticated: false, user: null });
        } catch (error: any) {
          console.error('Logout error:', error);
          set({ isAuthenticated: false, user: null });
          throw new Error(error.message || 'An error occurred during logout');
        }
      },

      setUser: (user) => set({
        user,
        isAuthenticated: !!user
      }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user
      })
    }
  )
);